import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { CustomTheme, Palette } from "../api/types";
import { useAuth, useErrorHandler } from "../auth/AuthContext";
import { BUILTIN_PALETTES } from "../lib/builtinPalettes";
import { customThemeRef } from "../lib/palette";
import { DEFAULT_THEME } from "../lib/themes";
import { AvatarPicker } from "./AvatarPicker";
import { ThemeEditor } from "./ThemeEditor";
import { ThemeImportModal } from "./ThemeImportModal";
import { ThemePicker } from "./ThemePicker";
import { ErrorMessage, Modal, Segmented } from "./common";

/** What ThemeEditor needs to open pre-filled with an imported theme. */
type ImportedTheme = { name: string; description: string; palette: Palette };

/**
 * The palette "Make your own" should open with: whatever the member is wearing
 * right now.
 *
 * Starting from a fixed default meant that building a variation on a theme you
 * liked began by throwing that theme away and re-entering it by eye. Picking
 * Rainbow and then Make your own should hand you Rainbow to change.
 *
 * The name is deliberately not carried over — a new palette needs its own, and
 * pre-filling "Rainbow" invites saving a second thing by that name. Returns
 * undefined when the current theme cannot be resolved (one deleted in another
 * tab, say), which lands on the editor's own blank default.
 */
function startingPointFrom(
  theme: string | undefined,
  customThemes: CustomTheme[],
): ImportedTheme | undefined {
  const builtin = BUILTIN_PALETTES.find((t) => t.id === (theme || DEFAULT_THEME));
  if (builtin) {
    return { name: "", description: "", palette: builtin.palette };
  }

  const own = customThemes.find((t) => customThemeRef(t.id) === theme);
  if (own) {
    return { name: "", description: own.description ?? "", palette: own.palette };
  }

  return undefined;
}

/** How long the "Saved!" note lingers. */
const SAVED_MS = 2000;

/** Only a child has both an icon and colours to choose between. */
type Tab = "icon" | "colors";

/**
 * Where appearance is chosen, for whoever opened it.
 *
 * Everything here is personal and nothing reaches anybody else: a member picks
 * their own palette, and a child their own icon. Parents and children see the
 * same colour controls, which is the point — colour was the last thing in this
 * app one person chose on another's behalf.
 *
 * A child sees icon and colours as separate tabs rather than one long stacked
 * scroll — the two are unrelated choices, and stacking them made "Colors" read
 * as a subsection of "Icon" instead of its own thing. A parent has no icon, so
 * there is nothing to tab between and the picker is all there is.
 *
 * Each choice saves as it is made rather than behind an "Apply" button: the
 * whole app repaints underneath the modal, so the preview *is* the result and a
 * confirm step would only hide it.
 */
export function AppearanceModal({ onClose }: { onClose: () => void }) {
  const { user, isParent, updateIdentity, customThemes, refreshCustomThemes } =
    useAuth();
  const toMessage = useErrorHandler();

  const [tab, setTab] = useState<Tab>("icon");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // null means closed; {theme: null} means "building a new one", optionally
  // pre-filled from `initial` when that new one came from Import.
  const [editor, setEditor] = useState<{
    theme: CustomTheme | null;
    initial?: ImportedTheme;
  } | null>(null);
  const [importing, setImporting] = useState(false);

  const savedTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  function flashSaved() {
    setSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), SAVED_MS);
  }

  /**
   * Runs one save. Nothing is applied optimistically: the server's response is
   * what lands in auth state, so what is on screen is always what was stored.
   */
  async function save(run: () => Promise<void>) {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await run();
      flashSaved();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Applies a theme name to the caller. Shared by the picker and by the editor,
   * which wants to put a member straight into the palette they just built.
   */
  async function wear(theme: string) {
    const next = await api.setTheme(theme);
    // theme is omitempty, so clearing it comes back absent — spell the empty
    // string out rather than letting it vanish.
    updateIdentity({ user: { ...next, theme: next.theme ?? "" } });
  }

  if (editor) {
    return (
      <ThemeEditor
        editing={editor.theme}
        {...(editor.initial ? { initial: editor.initial } : {})}
        onClose={() => setEditor(null)}
        onSaved={(themeToWear) =>
          void save(async () => {
            // The list first: wearing a palette the context has not heard of
            // yet would resolve to the default and repaint twice.
            await refreshCustomThemes();
            if (themeToWear) await wear(themeToWear);
            setEditor(null);
          })
        }
      />
    );
  }

  if (importing) {
    return (
      <ThemeImportModal
        onClose={() => setImporting(false)}
        onImported={(theme) => {
          setImporting(false);
          // Opens straight into the editor so whoever imported it can look it
          // over — and still tweak it — before it becomes one of their own.
          setEditor({ theme: null, initial: theme });
        }}
      />
    );
  }

  const title = isParent ? "Your look" : "Make it yours";
  const showingIcon = !isParent && tab === "icon";

  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack">
        <ErrorMessage>{error}</ErrorMessage>

        {!isParent ? (
          <Segmented<Tab>
            value={tab}
            onChange={setTab}
            options={[
              { value: "icon", label: "😊 Icon" },
              { value: "colors", label: "🎨 Colors" },
            ]}
          />
        ) : null}

        {showingIcon && user ? (
          <section className="stack">
            <p className="muted">This is you, all over the app.</p>
            <AvatarPicker
              value={user.avatar ?? ""}
              disabled={saving}
              onPick={(avatar) =>
                void save(async () => {
                  const next = await api.setChildAvatar(user.id, avatar);
                  updateIdentity({ user: next });
                })
              }
            />
          </section>
        ) : null}

        {!showingIcon && user ? (
          <section className="stack">
            <p className="muted">Just for you — nobody else's screen changes.</p>
            <ThemePicker
              value={user.theme ?? ""}
              customThemes={customThemes}
              disabled={saving}
              onPick={(theme) => void save(() => wear(theme))}
              onEdit={(theme) => setEditor({ theme })}
            />
            <div className="row">
              <button
                type="button"
                className="btn"
                disabled={saving}
                onClick={() => {
                  const from = startingPointFrom(user.theme, customThemes);
                  setEditor({ theme: null, ...(from ? { initial: from } : {}) });
                }}
              >
                🎨 Make your own
              </button>
              <button
                type="button"
                className="btn"
                disabled={saving}
                onClick={() => setImporting(true)}
              >
                📥 Import
              </button>
            </div>
          </section>
        ) : null}

        <div className="row row--between">
          <span className="appearance__status" aria-live="polite">
            {saving ? "Saving…" : saved ? "Saved!" : ""}
          </span>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
